import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import {
  chunkGalleryImages,
  classifyPhoto,
  GalleryLightbox,
  GalleryMosaicBlock,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

type GalleryFilter = 'all' | 'community' | 'apartment';

const FILTER_LABELS: Record<GalleryFilter, string> = {
  all: 'All',
  community: 'Community',
  apartment: 'Apartment',
};

/**
 * Unified photo gallery — all property photos in a repeating 2×2 + fifth
 * image layout: on mobile the fifth spans full width as a square; on desktop
 * it becomes a tall portrait beside the quad. Side alternates each block.
 */
export function PhotoGallery() {
  const images = useCollectionBinding<GalleryImage>('photogallery.images', {
    collection: 'photo_gallery',
  });

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
  }, [images]);

  const [activeFilter, setActiveFilter] = useState<GalleryFilter>('all');

  // Only show a filter chip when at least one photo matches that category.
  const availableFilters = useMemo<GalleryFilter[]>(() => {
    const present = new Set<'community' | 'apartment'>();
    for (const photo of photoList) {
      const category = classifyPhoto(photo);
      if (category === 'community' || category === 'apartment') {
        present.add(category);
      }
    }
    // Need at least two distinct categories for filtering to be meaningful.
    if (present.size < 2) return [];
    const filters: GalleryFilter[] = ['all'];
    if (present.has('community')) filters.push('community');
    if (present.has('apartment')) filters.push('apartment');
    return filters;
  }, [photoList]);

  const filteredPhotos = useMemo(() => {
    if (activeFilter === 'all') return photoList;
    return photoList.filter((photo) => classifyPhoto(photo) === activeFilter);
  }, [photoList, activeFilter]);

  const blocks = useMemo(() => chunkGalleryImages(filteredPhotos), [filteredPhotos]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-8 md:py-12">
        {availableFilters.length > 0 && (
          <div className="gallery-filters" role="tablist" aria-label="Filter gallery by category">
            {availableFilters.map((filter) => {
              const isActive = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveFilter(filter)}
                  className={`gallery-filter${isActive ? ' gallery-filter--active' : ''}`}
                >
                  {FILTER_LABELS[filter]}
                </button>
              );
            })}
          </div>
        )}

        <div className="gallery-mosaic-stack">
          {blocks.map((block, blockIndex) => (
            <GalleryMosaicBlock
              key={blockIndex}
              images={block}
              startIndex={blockIndex * 5}
              tallOnRight={blockIndex % 2 === 0}
              onSelect={setSelectedIndex}
            />
          ))}
        </div>
      </div>

      <GalleryLightbox
        images={filteredPhotos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .gallery-filters {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .gallery-filter {
          appearance: none;
          border: 2px solid var(--vibe-color-primary);
          background: transparent;
          color: var(--vibe-color-primary);
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 0.75rem 2rem;
          border-radius: 9999px;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
          -webkit-user-select: none;
          user-select: none;
        }

        .gallery-filter:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
        }

        .gallery-filter:active {
          transform: scale(0.97);
        }

        .gallery-filter--active,
        .gallery-filter--active:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
        }

        @media (max-width: 639px) {
          .gallery-filters {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
          }

          .gallery-filter {
            width: 100%;
            font-size: 0.9375rem;
            padding: 1rem 1.5rem;
            letter-spacing: 0.06em;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoGallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'photogallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'All photos displayed in the alternating mosaic gallery layout.',
    },
  },
};

(PhotoGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PhotoGallery;
